import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { allowsFunctionalStorage } from "@/lib/cookieConsent";

const ThemeContext = createContext({ theme: "dark", toggle: () => {}, setTheme: () => {} });

const STORAGE_KEY = "autopark_theme";
const USER_SET_KEY = "autopark_theme_user_set";

const getSystemTheme = () => {
  try {
    if (typeof window !== "undefined" && window.matchMedia) {
      return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
    }
  } catch {}
  return "dark";
};

const applyTheme = (theme) => {
  const root = document.documentElement;
  if (theme === "light") {
    root.classList.add("light");
    root.classList.remove("dark");
    root.style.colorScheme = "light";
  } else {
    root.classList.remove("light");
    root.classList.add("dark");
    root.style.colorScheme = "dark";
  }
};

export function ThemeProvider({ children }) {
  // Első betöltés: mentett érték > OS prefers-color-scheme > 'dark'
  const [theme, setThemeState] = useState(() => {
    if (!allowsFunctionalStorage()) return getSystemTheme();
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === "light" || saved === "dark") return saved;
    } catch {}
    return getSystemTheme();
  });

  useEffect(() => {
    applyTheme(theme);
    if (!allowsFunctionalStorage()) return;
    try { localStorage.setItem(STORAGE_KEY, theme); } catch {}
  }, [theme]);

  // OS téma követése, amíg a látogató NEM választott manuálisan
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = (e) => {
      if (!allowsFunctionalStorage()) {
        setThemeState(e.matches ? "light" : "dark");
        return;
      }
      try {
        if (localStorage.getItem(USER_SET_KEY) === "1") return; // manuálisan választott, ne írjuk felül
      } catch {}
      setThemeState(e.matches ? "light" : "dark");
    };
    // Régebbi Safari kompat
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);

  const markUserSet = () => {
    if (!allowsFunctionalStorage()) return;
    try { localStorage.setItem(USER_SET_KEY, "1"); } catch {}
  };

  const setTheme = useCallback((next) => {
    markUserSet();
    setThemeState(next === "light" ? "light" : "dark");
  }, []);
  const toggle = useCallback(() => {
    markUserSet();
    setThemeState((t) => (t === "light" ? "dark" : "light"));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggle, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
